/* eslint-disable @typescript-eslint/no-explicit-any */

import { TimeType } from "../../shared/constants/time-type.enum";

export const convertTimeToSeconds = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return (hours * 3600) + (minutes * 60) + seconds;
}

export const calculateFinalDate = (startDate: string, expires: number, timeType: TimeType): string => {
  const initialDate = new Date(startDate);
  const finalDate = new Date(initialDate);

  switch (timeType) {
    case TimeType.DAYS:
      finalDate.setDate(finalDate.getDate() + expires);
      break;
    case TimeType.MONTHS:
      finalDate.setMonth(finalDate.getMonth() + expires);
      break;
    case TimeType.YEARS:
      finalDate.setFullYear(finalDate.getFullYear() + expires);
      break;
  }

  return finalDate.toISOString();
}

export const calculateDurationUnlimited = (expires: number, timeType: TimeType): string => {
  let businessDays = 0;

  if (timeType === TimeType.MONTHS) {
    businessDays = expires * 22;
  } else if (timeType === TimeType.DAYS) {
    businessDays = expires;
  } else if (timeType === TimeType.YEARS) {
    businessDays = expires * 252;
  }

  const totalHours = businessDays * 8;

  return `${pad(totalHours)}:00:00`;
}

export const calculateRestTime = (startTime: any, endTime: any): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '00:00:00';
  }

  const durationInMilliseconds = end.getTime() - start.getTime();

  if (durationInMilliseconds > 0) {
    const remainingHours = Math.floor(durationInMilliseconds / (1000 * 60 * 60));
    const remainingMinutes = Math.floor((durationInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const remainingSeconds = Math.floor((durationInMilliseconds % (1000 * 60)) / 1000);

    return `${remainingHours}:${pad(remainingMinutes)}:${pad(remainingSeconds)}`;
  }
  return '00:00:00';
}

export const formatIsoDateToLocal = (isoDate: string): string => {
  const date = new Date(isoDate);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export const formatDateToPortuguese = (dateString: string): string => {
  const date = new Date(dateString);

  let formatted = new Intl.DateTimeFormat('pt-PT', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'Europe/Lisbon'
  }).format(date);

  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

  formatted = formatted.replace(/ de (\w+)/, (_, month) => ` de ${month.charAt(0).toUpperCase() + month.slice(1)}`);

  return formatted;
};

export const pad = (num: number): string => {
  return num < 10 ? '0' + num : num.toString();
}

export const addTimes = (time1: string, time2: string): string => {
    const [hours1, minutes1, seconds1] = time1.split(':').map(Number);
    const [hours2, minutes2, seconds2] = time2.split(':').map(Number);

    let totalSeconds = seconds1 + seconds2;
    let totalMinutes = minutes1 + minutes2 + Math.floor(totalSeconds / 60);
    const totalHours = hours1 + hours2 + Math.floor(totalMinutes / 60);

    totalSeconds = totalSeconds % 60;
    totalMinutes = totalMinutes % 60;

    return `${pad(totalHours)}:${pad(totalMinutes)}:${pad(totalSeconds)}`;
  }

export const reduceTimes = (time1: string, time2: string): string => {
    const [hours1, minutes1, seconds1] = time1.split(':').map(Number);
    const [hours2, minutes2, seconds2] = time2.split(':').map(Number);

    const total1 = hours1 * 3600 + minutes1 * 60 + seconds1;
    const total2 = hours2 * 3600 + minutes2 * 60 + seconds2;

    if (total1 <= total2) {
      return '00:00:00';
    }

    const diff = total1 - total2;

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

export const finalTimeForClient = (oldAvailableTime: string, restTime: any, isAdd: boolean): string => {
  if (!isAdd) {
    return reduceTimes(oldAvailableTime, restTime);
  }
  return addTimes(oldAvailableTime, restTime);
}

export const convertTimeToMilliseconds = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000) + (seconds * 1000);
}

export const calculateRestTimeSupport = (availableTime: string, contractTime: string): string => {
  const availableMilliseconds = convertTimeToMilliseconds(availableTime);
  const contractMilliseconds = convertTimeToMilliseconds(contractTime);

  if (availableMilliseconds > contractMilliseconds) {
    return contractTime;
  }

  return availableTime;
}

export const isExpired = (date: string): boolean => {
  const scheduledDate = new Date(date);
  const currentDate = new Date();

  return scheduledDate < currentDate;
}

export const isPastOneDay = (date: string): boolean => {
  const scheduledDate = new Date(date);
  const currentDate = new Date();

  const differenceInTime = currentDate.getTime() - scheduledDate.getTime();
  const oneDayInMilliseconds = 24 * 60 * 60 * 1000;

  return differenceInTime >= oneDayInMilliseconds;
}

export const isWithinTwoDays = (availableFrom: string): boolean => {
  const scheduledDate = new Date(availableFrom);
  const currentDate = new Date();

  const twoDaysFromNow = new Date();
  twoDaysFromNow.setDate(currentDate.getDate() + 2);

  return scheduledDate <= twoDaysFromNow;
}

export const isLessThanOneHour = (dateString: string): boolean => {
  const targetDate = new Date(dateString).getTime();
  const now = new Date().getTime();

  const diffInMs = targetDate - now;

  return diffInMs > 0 && diffInMs < 60 * 60 * 1000;
};
